import Extension from '../base/extension';
import Proposition from '../base/proposition';

/**
 * The PropositionsManager is responsible for managing propositions, which are
 * a way to encapsulate rules and actions into a single unit. Propositions are
 * registered with the manager, and can be triggered by events.
 */
class PropositionsManager extends Extension {
  propositions = []; // Array of registered propositions
  triggers = new Map(); // Map of event names to arrays of handlers
  running = false; // Flag to prevent multiple queue processing
  queue = []; // Queue of events to process

  constructor(name) {
    super(name);
  }

  /**
   * Register a new proposition
   * 
   * @param {Object|Array} spec a JSON object (or array of objects) representing a proposition, following a specific schema.
   */
  register(spec = []) {
    let specs = Array.isArray(spec) ? spec : [spec];

    specs.forEach(spec => {
      let proposition = new Proposition(spec);
      proposition.config.triggers.forEach(trigger => {
        this.registerEventHandler(trigger, (...args) => proposition.handleEvent(...args));
      });
      this.propositions.push(proposition);
    });
  }

  /**
   * Register an event handler to respond to a specific event.
   * 
   * For propositions, the 'triggers' array property on the proposition's config
   * is used to determine which events to listen for.
   * 
   * Note the queuing here - we don't immediately handle an event, but instead
   * queue it up for processing. This is to ensure that we don't run into any
   * issues with event handlers being added or removed during the handling of
   * an event.
   * 
   * @param {String} eventName 
   * @param {Function} handler 
   */
  registerEventHandler(eventName, handler) {
    if (!this.triggers.has(eventName)) {
      this.triggers.set(eventName, []);
      document.addEventListener(eventName, e => this.queueEvent(e, eventName));
    }
    this.triggers.get(eventName).push(handler);
  }

  /**
   * Queue an event for processing, and initiate the queue processing.
   * 
   * @param {Event} e 
   * @param {String} eventName 
   */
  queueEvent(e, eventName) {
    console.log(`${this.logPrefix}`, `Queueing event`, eventName);
    this.queue.push({ e, eventName });
    this.processQueue();
  }

  /**
   * Process the event queue in FIFO order.
   * 
   * @returns undefined
   */
  async processQueue() {
    if (this.running) return;
    this.running = true;
    
    console.log(`${this.logPrefix}`, `Starting queue processing`);

    while (this.queue.length > 0) {
      let { e, eventName } = this.queue.shift();
      console.log(`${this.logPrefix}`, `Handling event '${eventName}'`);
      await this.handleEvent(e, eventName);
      console.log(`${this.logPrefix}`, `Finished handling event '${eventName}'`);
    }

    console.log(`${this.logPrefix}`, `Completed queue processing`);
    this.running = false;
  }

  /**
   * Run all handlers for a given event sequentially.
   * 
   * A single event might have many handlers registered (e.g. multiple actions
   * on a single proposition, or multiple propositions listening for the same
   * event). This method ensures that all handlers are run in sequence.
   * 
   * @param {Event} e 
   * @param {String} eventName 
   */
  async handleEvent(e, eventName) {
    //console.log(`Handling event '${eventName}'`);
    for (const handler of this.triggers.get(eventName)) {
      await handler(e, eventName);
    }
  }

  /**
   * Describe registered propositions and their rules / actions in the console.
   */
  async explain() {
    for (const proposition of this.propositions) {
      await proposition.explain();
    }
  }
}

export default PropositionsManager;